import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useFamily, usePlan, useUser } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { Copy, copyText } from "@/copy/copy";
import { ThemeButton } from "@/components/ThemeButton";
import { CheckInModal, useCheckInDue } from "@/checkin/CheckInModal";
import { GuideProvider } from "@/guides/GuideProvider";
import { AssistantProvider } from "@/assistant/AssistantProvider";
import { isSet, todayLocal } from "@/plan-logic/lib/dates";
import { hasShortfall } from "@/plan-logic/plan-view/shortfall";
import { AppDataProvider } from "./AppData";
import { MobileTabs } from "./MobileTabs";
import { appPath, NAV } from "./nav";
import { useBackStack } from "./navHistory";
import { isRebuilding, rebuildFailed, usePlanUpdatedToast } from "./PlanRebuildStatus";
import "@/styles/v47-shell.css";

function initials(email: string | null | undefined, given?: string, family?: string): string {
  if (given) return `${given[0] ?? ""}${family?.[0] ?? ""}`.toUpperCase();
  return (email ?? "?").slice(0, 2).toUpperCase();
}

/** A settings gear, drawn like the phone tab bar's icons (MobileTabs). */
function Gear() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * The settings gear's menu (prototype v43's profile menu), mapped to what exists: Settings holds the
 * account, the password, and the employer code or product key (and the upgrade); support is the
 * site's Contact page; the landing page is the site's home. v43's Notifications, Budget alerts, and
 * Download my data have nothing behind them yet and are left out.
 */
function ProfileMenu({
  label,
  name,
  email,
  isAdmin,
}: {
  label: string;
  name: string;
  email: string;
  isAdmin: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // The nav row scrolls sideways (overflow-x), which also clips anything below it, so the menu is
  // `position: fixed` and placed under the gear, following it on scroll and resize.
  const [place, setPlace] = useState<{ top: number; right: number }>();
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = button.current?.getBoundingClientRect();
      if (r)
        setPlace({
          top: r.bottom + 8,
          right: Math.max(12, document.documentElement.clientWidth - r.right),
        });
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const close = () => setOpen(false);
  return (
    <div className="wp-profile" ref={ref}>
      <button
        ref={button}
        type="button"
        className="avbtn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Settings"
        title="Settings"
        onClick={() => setOpen((o) => !o)}
        data-testid="profile-menu"
      >
        <Gear />
      </button>
      {open && (
        <div className="profmenu" role="menu" style={place} data-testid="profile-menu-items">
          <div className="pmhead">
            <span className="av" aria-hidden="true">
              {label}
            </span>
            <div>
              {name && <b>{name}</b>}
              <span>{email}</span>
            </div>
          </div>
          <Link role="menuitem" to="/app/settings" onClick={close}>
            Account details
          </Link>
          <Link role="menuitem" to="/app/settings#password" onClick={close}>
            Password and login
          </Link>
          <Link role="menuitem" to="/app/settings#plan" onClick={close}>
            Plan and billing
          </Link>
          <Link role="menuitem" to="/contact" onClick={close}>
            Contact support
          </Link>
          {isAdmin && (
            <Link role="menuitem" to="/admin" onClick={close}>
              <Copy k="nav.admin" />
            </Link>
          )}
          <Link role="menuitem" to="/" onClick={close}>
            Open landing page
          </Link>
          <div className="pmsep" role="separator" />
          <button
            role="menuitem"
            type="button"
            className="pmout"
            data-testid="sign-out"
            onClick={async () => {
              close();
              // Leave the signed-in pages first: signed out on them, the sign-in guard would redirect to
              // /signin?next=... and race this navigation.
              await navigate("/", { replace: true });
              await signOut();
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

/** The signed-in app chrome: the prototype's top bar, plus the bottom tab bar on phones. */
export function AppShell() {
  const { user: fbUser } = useAuth();
  const userQuery = useUser();
  const familyQuery = useFamily();
  const planQuery = usePlan();
  const location = useLocation();
  const { canGoBack, back } = useBackStack();

  const sub = location.pathname.replace(/^\/(dev\/)?app\/?/, "").split("/")[0];
  const active = NAV.find((n) => n.path === sub)?.key ?? "home";
  // Actions is always in the menu (v42); it carries the warning mark while the plan runs short.
  const short = hasShortfall(planQuery.data?.plans?.abiPlan);
  const self = familyQuery.data?.family?.persons[0];
  const isAdmin = !!(userQuery.data?.isAdmin || userQuery.data?.isSuperAdmin);
  usePlanUpdatedToast(
    isRebuilding(familyQuery.data, planQuery.data),
    rebuildFailed(familyQuery.data, planQuery.data),
    sub === "update-data",
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // The check-in opens once per visit when the plan has something to ask about.
  const abi = planQuery.data?.plans?.abiPlan;
  const currentDate = userQuery.data?.currentDate;
  const today = useMemo(() => (isSet(currentDate) ? currentDate : todayLocal()), [currentDate]);
  // Not in the dev gallery unless asked (`?checkin`): it would cover the page being looked at. (Its
  // timer used to be cancelled for good by StrictMode's second effect run, so it never showed there.)
  const gallery =
    location.pathname.startsWith("/dev/") && !new URLSearchParams(location.search).has("checkin");
  const due = useCheckInDue(familyQuery.data, abi, today) && !gallery;
  const [checkIn, setCheckIn] = useState(false);
  const [checkInDone, setCheckInDone] = useState(false);
  const asked = useRef(false);
  useEffect(() => {
    if (!due || asked.current) return;
    // marked asked when it opens, so StrictMode's second effect run doesn't cancel it for good
    const t = setTimeout(() => {
      asked.current = true;
      setCheckIn(true);
    }, 700);
    return () => clearTimeout(t);
  }, [due]);

  const email = fbUser?.email ?? userQuery.data?.email ?? "";
  const label = initials(email, self?.givenName, self?.familyName);
  return (
    <div id="app">
      <GuideProvider uid={fbUser?.uid} hold={checkIn || (due && !checkInDone)}>
        <AssistantProvider>
          <div className="nav">
            <div className="nav-in">
              <button
                type="button"
                className="navback"
                onClick={back}
                aria-label="Back to the last page you were on"
                hidden={!canGoBack}
                data-testid="nav-back"
              >
                &#8592;
              </button>
              <Link to="/app" className="brand" aria-label="WealthyPlanet home">
                <span className="wplock" aria-hidden="true" />
              </Link>
              <div className="tabwrap">
                {NAV.filter((n) => n.tab).map((n) => (
                  <NavLink
                    key={n.key}
                    to={appPath(n.path)}
                    end={n.path === ""}
                    className={({ isActive }) =>
                      `tab${isActive ? " on" : ""}${n.key === "actions" && short ? " warn-tab" : ""}`
                    }
                    data-testid={`tab-${n.key}`}
                  >
                    {copyText(n.label)}
                  </NavLink>
                ))}
              </div>
              <div className="r">
                <ThemeButton id="thm-app" />
                <ProfileMenu
                  label={label}
                  name={[self?.givenName, self?.familyName].filter(Boolean).join(" ")}
                  email={email}
                  isAdmin={isAdmin}
                />
              </div>
            </div>
          </div>
          <main>
            <AppDataProvider user={userQuery} family={familyQuery} plan={planQuery}>
              <Outlet />
            </AppDataProvider>
          </main>
          {checkIn && familyQuery.data && abi && (
            <CheckInModal
              doc={familyQuery.data}
              abi={abi}
              today={today}
              onClose={() => {
                setCheckIn(false);
                setCheckInDone(true);
              }}
            />
          )}
          <MobileTabs activeKey={active} short={short} />
        </AssistantProvider>
      </GuideProvider>
    </div>
  );
}
